import { ChevronDownIcon, SearchIcon } from "@chakra-ui/icons";
import {
  Box,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  ListItem,
  Spacer,
  Text,
  UnorderedList,
  useOutsideClick,
} from "@chakra-ui/react";
import { useCombobox, useMultipleSelection } from "downshift";
import React, { Fragment, useCallback, useMemo, useRef } from "react";
import { RiCheckLine } from "react-icons/ri";
import {
  BaseGenericType,
  MultiSelectMenuBaseProps,
  SelectAllItem,
} from "./types";

const SELECT_ALL_ID = "__select-all__";

export interface CheckMultiSelectMenuProps<T>
  extends Omit<MultiSelectMenuBaseProps<T>, "initialValues"> {
  renderDisplayText?: (selectedItems: T[]) => string;
  selectAllItem?: SelectAllItem;
  selectedItems: T[];
}

export function CheckMultiSelectInput<T extends BaseGenericType>({
  size,
  options,
  selectedItems,
  onChange,
  isLoading,
  labelAttr = "label",
  renderDropdownItem = (item, _keyword) => <Box>{item[labelAttr]}</Box>,
  placeholder = "Select multiple",
  onAddTag,
  onRemoveTag,
  isDisabled = false,
  containerProps,
  inputContainerProps,
  label,
  labelProps,
  error,
  openDirection = "down",
  maxMenuHeight,
  renderDisplayText,
  leftIcon,
  selectAllItem,
}: CheckMultiSelectMenuProps<T>) {
  const ref = useRef<HTMLDivElement>(null);

  useOutsideClick({
    ref: ref,
    handler: () => isOpen && closeMenu(),
  });

  const [inputValue, setInputValue] = React.useState<string>("");
  const hasSelectAll = selectAllItem?.enable === true;

  const selectedItemsById = useMemo(
    () => new Set(selectedItems.map((item) => item.id)),
    [selectedItems]
  );

  const allSelected = useMemo(
    () =>
      options.length > 0 &&
      options.every((option) => selectedItemsById.has(option.id)),
    [options, selectedItemsById]
  );

  const displayText = useMemo(() => {
    if (selectedItems.length === 0) return "";
    if (allSelected && hasSelectAll) return selectAllItem.text;
    if (renderDisplayText) return renderDisplayText(selectedItems);
    return selectedItems.map((item) => item[labelAttr]).join(", ");
  }, [
    selectedItems,
    allSelected,
    hasSelectAll,
    selectAllItem,
    renderDisplayText,
    labelAttr,
  ]);

  const getFilteredItemsForDropdown = useCallback(
    (selectedItems: T[], inputValue: string) => {
      const lowerCasedInputValue = inputValue?.toLowerCase() || "";
      return (options || []).filter(function filterItemsForDropdown(item) {
        return (
          item[labelAttr]?.toLowerCase().includes(lowerCasedInputValue) ?? false
        );
      });
    },
    [options, labelAttr]
  );

  const filteredItems: T[] = React.useMemo(() => {
    const notNullItems = selectedItems?.filter((i): i is T => !!i);
    return getFilteredItemsForDropdown(notNullItems, inputValue);
  }, [getFilteredItemsForDropdown, selectedItems, inputValue]);

  const isSearching = inputValue.length > 0;

  const itemsForDropdown: T[] = useMemo(() => {
    if (hasSelectAll && !isSearching) {
      return [
        { id: SELECT_ALL_ID, [labelAttr]: selectAllItem.text } as T,
        ...filteredItems,
      ];
    }
    return filteredItems;
  }, [filteredItems, hasSelectAll, isSearching, labelAttr, selectAllItem]);

  const { getDropdownProps } = useMultipleSelection({
    selectedItems,
    onStateChange({ selectedItems: newSelectedItems, type }) {
      switch (type) {
        case useMultipleSelection.stateChangeTypes.SelectedItemKeyDownBackspace:
        case useMultipleSelection.stateChangeTypes.SelectedItemKeyDownDelete:
        case useMultipleSelection.stateChangeTypes.DropdownKeyDownBackspace:
        case useMultipleSelection.stateChangeTypes.FunctionRemoveSelectedItem:
          onChange?.(
            newSelectedItems?.filter((i): i is T => i !== undefined) ?? []
          );
          break;
        default:
          break;
      }
    },
  });

  const handleSelectAllToggle = useCallback(() => {
    if (allSelected) {
      onChange?.([]);
    } else {
      onChange?.([...options]);
    }
  }, [allSelected, options, onChange]);

  const {
    isOpen,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getItemProps,
    openMenu,
    closeMenu,
    getLabelProps,
  } = useCombobox({
    items: itemsForDropdown,
    itemToString(item) {
      return item ? item[labelAttr] : "";
    },
    defaultHighlightedIndex: 0,
    selectedItem: null,
    stateReducer(state, actionAndChanges) {
      const { changes, type } = actionAndChanges;

      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          return {
            ...changes,
            isOpen: true,
            inputValue: "",
            highlightedIndex: state.highlightedIndex,
          };
        case useCombobox.stateChangeTypes.InputBlur:
          return {
            ...changes,
            isOpen: true,
          };
        default:
          return changes;
      }
    },
    async onStateChange({
      inputValue: newInputValue,
      type,
      selectedItem: newSelectedItem,
    }) {
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          if (!newSelectedItem) break;

          // Handle select-all sentinel
          if (newSelectedItem.id === SELECT_ALL_ID) {
            handleSelectAllToggle();
            setInputValue("");
            break;
          }

          const isItemAlreadySelected = selectedItems.some(
            (item) => item.id === newSelectedItem.id
          );

          if (isItemAlreadySelected) {
            const nextItems = selectedItems.filter(
              (item) => item.id !== newSelectedItem.id
            );
            if (onRemoveTag) {
              onRemoveTag(newSelectedItem);
            } else if (onChange) {
              onChange(nextItems);
            }
          } else {
            const nextItems = [...selectedItems, newSelectedItem];
            if (onAddTag) {
              onAddTag(newSelectedItem);
            } else if (onChange) {
              onChange(nextItems);
            }
          }
          setInputValue("");
          break;

        case useCombobox.stateChangeTypes.InputChange:
          openMenu();
          setInputValue(newInputValue ?? "");
          break;

        case useCombobox.stateChangeTypes.InputBlur:
          closeMenu();
          break;

        default:
          break;
      }
    },
  });

  const openMenuUnlessDisabled = useCallback(() => {
    if (!isDisabled) {
      openMenu();
    }
  }, [isDisabled, openMenu]);

  const inputProps = getInputProps(
    getDropdownProps({ preventKeyAction: true })
  );

  function* renderDropdownItems() {
    let group = "";
    for (const [index, item] of itemsForDropdown?.entries() ?? []) {
      // Render select-all sentinel
      if (item.id === SELECT_ALL_ID && selectAllItem !== undefined) {
        yield (
          <Fragment key={SELECT_ALL_ID}>
            <ListItem
              backgroundColor={
                highlightedIndex === index ? "gray.200" : "white"
              }
              listStyleType="none"
              cursor="pointer"
              {...getItemProps({ item, index })}
            >
              <Flex align="center" minHeight={12} padding={2}>
                <Text>{selectAllItem.text}</Text>
                {allSelected ? (
                  <>
                    <Spacer />
                    <Icon as={RiCheckLine} aria-hidden />
                  </>
                ) : null}
              </Flex>
            </ListItem>
            <Divider />
          </Fragment>
        );
        continue;
      }

      const isItemSelected = selectedItemsById.has(item.id);

      if (item.group && item.group !== group) {
        group = item.group;
        yield (
          <Fragment key={`group-${group}`}>
            {index > 0 && <Divider />}
            <ListItem backgroundColor="white" listStyleType="none">
              <Flex align="center" minHeight={12} padding={2}>
                <Text fontSize="sm" fontWeight="bold">
                  {group} tags
                </Text>
              </Flex>
            </ListItem>
          </Fragment>
        );
      }
      yield (
        <ListItem
          key={item.id}
          backgroundColor={highlightedIndex === index ? "gray.200" : "white"}
          listStyleType="none"
          cursor="pointer"
          {...getItemProps({
            item,
            index,
            "aria-selected": isItemSelected,
          })}
        >
          <Flex align="center" minHeight={12} padding={2}>
            {renderDropdownItem(item, inputValue)}
            {isItemSelected ? (
              <>
                <Spacer />
                <Icon as={RiCheckLine} aria-hidden />
              </>
            ) : null}
          </Flex>
        </ListItem>
      );
    }
  }

  return (
    <FormControl
      as={Flex}
      flexDirection="column"
      gap="2"
      padding="1px"
      ref={ref}
      isInvalid={error !== undefined}
      {...containerProps}
    >
      {label !== undefined ? (
        <FormLabel margin="0" {...getLabelProps()} {...labelProps}>
          {label}
        </FormLabel>
      ) : null}
      <Box position="relative" {...inputContainerProps}>
        <InputGroup
          onClick={openMenuUnlessDisabled}
          onFocus={openMenuUnlessDisabled}
          display="flex"
          justifyContent="center"
        >
          {leftIcon === null ? null : (
            <InputLeftElement
              children={leftIcon ?? <SearchIcon color="gray.500" />}
            />
          )}

          <Input
            isDisabled={isDisabled}
            {...inputProps}
            value={!isOpen ? displayText : inputProps.value || ""}
            placeholder={placeholder}
            size={size}
            sx={{
              textOverflow: "ellipsis",
              overflow: "hidden",
              whiteSpace: "nowrap",
            }}
            aria-label={
              typeof label === "string" ? label : "Select multiple options"
            }
          />

          <InputRightElement children={<ChevronDownIcon color="gray.500" />} />
        </InputGroup>

        <UnorderedList
          {...getMenuProps()}
          aria-multiselectable="true"
          display={isOpen ? "block" : "none"}
          borderRadius="md"
          position="absolute"
          background="white"
          marginLeft={0}
          zIndex="200"
          boxShadow="none"
          width="100%"
          overflow="scroll"
          maxHeight={maxMenuHeight || "280px"}
          bottom={openDirection === "up" ? "100%" : undefined}
          top={openDirection === "down" ? "100%" : undefined}
          border="1px solid"
          borderColor="gray.200"
          borderTopWidth="0"
        >
          {[...renderDropdownItems()]}
        </UnorderedList>
      </Box>
      {error !== undefined ? (
        <FormErrorMessage margin="0">{error}</FormErrorMessage>
      ) : null}
    </FormControl>
  );
}
